import React, { useMemo, useState } from 'react';
import MDBox from 'src/components/md/MDBox';
import MDTypography from 'src/components/md/MDTypography';
import { CaretUpIcon, CaretDownIcon } from '@phosphor-icons/react';
import EdiTransactionViewer from './EdiTransactionViewer';
import { Num, T, useGT } from 'gt-react';

interface EdiOrderHistoryProps {
  orderful850Payload: Record<string, unknown>;
  orderful860Payloads: Record<string, unknown>[];
}

interface HistoryEntry {
  kind: '850' | '860';
  changeNumber?: number;
  date: string | null;
  orderfulTransactionId: string | null;
  payload: Record<string, unknown>;
}

function extractDate(payload: Record<string, unknown>): string | null {
  if (payload.createdAt && typeof payload.createdAt === 'string') {
    return new Date(payload.createdAt).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  }
  return null;
}

function extractOrderfulId(payload: Record<string, unknown>): string | null {
  if (payload.id && typeof payload.id === 'string') {
    return payload.id;
  }
  return null;
}

export function EdiOrderHistory({
  orderful850Payload,
  orderful860Payloads,
}: EdiOrderHistoryProps) {
  const gt = useGT();
  const [isOpen, setIsOpen] = useState(false);
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  const entries: HistoryEntry[] = useMemo(
    () => [
      {
        kind: '850' as const,
        date: extractDate(orderful850Payload),
        orderfulTransactionId: extractOrderfulId(orderful850Payload),
        payload: orderful850Payload,
      },
      ...orderful860Payloads.map((payload, i) => ({
        kind: '860' as const,
        changeNumber: i + 1,
        date: extractDate(payload),
        orderfulTransactionId: extractOrderfulId(payload),
        payload,
      })),
    ],
    [orderful850Payload, orderful860Payloads],
  );

  return (
    <MDBox mt={1}>
      <MDBox
        onClick={() => setIsOpen(!isOpen)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          cursor: 'pointer',
          p: 0.5,
          borderRadius: '4px',
          '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' },
        }}
      >
        {isOpen ? <CaretUpIcon size={16} /> : <CaretDownIcon size={16} />}
        <MDTypography variant="caption" fontWeight="bold">
          <T context="EDI order history section title with entry count">
            EDI Order History (<Num>{entries.length}</Num>)
          </T>
        </MDTypography>
      </MDBox>
      {isOpen && (
        <MDBox
          sx={{
            borderLeft: '2px solid',
            borderColor: 'grey.300',
            pl: 1.5,
            ml: 1,
            mt: 0.5,
          }}
        >
          {entries.map((entry, index) => {
            const isExpanded = expandedIndex === index;
            return (
              <MDBox key={index} mb={0.5}>
                <MDBox
                  onClick={() => setExpandedIndex(isExpanded ? null : index)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    cursor: 'pointer',
                    py: 0.25,
                    px: 0.5,
                    borderRadius: '4px',
                    position: 'relative',
                    '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' },
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      left: -16,
                      top: '50%',
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      backgroundColor:
                        index === 0 ? 'primary.main' : 'grey.400',
                      transform: 'translateY(-50%)',
                    },
                  }}
                >
                  {isExpanded ? (
                    <CaretUpIcon size={14} />
                  ) : (
                    <CaretDownIcon size={14} />
                  )}
                  <MDTypography
                    variant="caption"
                    fontWeight="medium"
                    sx={{ flex: 1, fontSize: '0.7rem' }}
                  >
                    {entry.kind === '850' ? (
                      <T context="EDI 850 original purchase order history row">
                        850 - Original PO
                      </T>
                    ) : (
                      <T context="EDI 860 change order history row">
                        860 - Change #<Num>{entry.changeNumber ?? 0}</Num>
                      </T>
                    )}
                  </MDTypography>
                  {entry.date && (
                    <MDTypography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: '0.65rem' }}
                    >
                      {entry.date}
                    </MDTypography>
                  )}
                  {entry.orderfulTransactionId && (
                    <MDTypography
                      component="a"
                      variant="caption"
                      color="info"
                      href={`https://ui.orderful.com/transactions/${entry.orderfulTransactionId}/print-view`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e: React.MouseEvent) => e.stopPropagation()}
                      sx={{
                        fontSize: '0.65rem',
                        textDecoration: 'none',
                        '&:hover': { textDecoration: 'underline' },
                      }}
                    >
                      {gt('Orderful', {
                        context:
                          'link label to Orderful transaction print view',
                      })}
                    </MDTypography>
                  )}
                </MDBox>
                {isExpanded && (
                  <MDBox
                    sx={{
                      mt: 0.5,
                      border: '1px solid',
                      borderColor: 'grey.200',
                      borderRadius: 1,
                      overflow: 'auto',
                      maxHeight: 400,
                    }}
                  >
                    <EdiTransactionViewer
                      ordefulMessage={{ payload: entry.payload }}
                    />
                  </MDBox>
                )}
              </MDBox>
            );
          })}
        </MDBox>
      )}
    </MDBox>
  );
}
